import { useQuery } from "@tanstack/react-query"
import { useMemo } from "react"
import { IAccountBookPairs, TradingBook } from "../../types"
import { useApi } from "../../../../hooks/useApi"

const useBooksQuery = (
  { accountIds }: { accountIds?: number[] } = {},
  { enabled = true } = {}, // Allow passing enabled option. True by default
) => {
  const { scientApi } = useApi()

  const { data, error, isLoading, isSuccess } = useQuery(
    ["tradingbooks", accountIds],
    () => scientApi.trading.getBooks({ accountIds }),
    {
      staleTime: Infinity,
      refetchOnWindowFocus: false, // to avoid rerender when there's no books associated to an user
      enabled,
    },
  )

  /**
   * Generate the default account book pairs for the default filter rules.
   */
  const defaultAccountBookPairs = useMemo<IAccountBookPairs>(() => {
    const books = (data || []) as TradingBook[]
    const accountToBooks = new Map<number, Set<number>>()
    for (const book of books) {
      if (!book?.is_default_filter || !book?.default_filter_account_ids?.length) continue
      for (const accountId of book.default_filter_account_ids) {
        if (!accountToBooks.has(accountId)) accountToBooks.set(accountId, new Set<number>())
        accountToBooks.get(accountId)!.add(book.id)
      }
    }
    return Array.from(accountToBooks.entries()).map(([account_id, bookIdSet]) => ({
      account_id,
      book_ids: Array.from(bookIdSet).sort((a, b) => a - b),
    }))
  }, [data])

  return {
    books: useMemo(() => data || [], [data]),
    isLoading: enabled ? isLoading : false, // hack because when 'enabled' is false, 'isLoading' is true instead of false
    isSuccess,
    error,
    defaultAccountBookPairs,
  }
}

export default useBooksQuery
